import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Zap, Rocket, Crown, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../../../context/LangContext';
import { useCurrency } from '../../../context/CurrencyContext';
import { staggerContainer, staggerChild, SPRING } from '../../../animations/variants';

export const TextAgentPricing = ({ isAnnual }) => {
  const { t, lang } = useLang();
  const { currency, getPrice, getCurrencySymbol } = useCurrency();

  const plans = [
    {
      name: "Starter",
      priceKey: isAnnual ? 'starterAnnual' : 'starter',
      annualTotalMAD: "9 588 MAD/an",
      annualTotalEUR: "852 €/an",
      annualTotalUSD: "948 $/an",
      features: [
        lang === 'fr' ? "Agent IA avec limite de messages" : "AI Agent with message limit",
        lang === 'fr' ? "Dashboard basique" : "Basic dashboard",
        lang === 'fr' ? "Support email" : "Email support",
        lang === 'fr' ? "1 000 conversations / mois" : "1,000 conversations / month",
      ],
      icon: Zap,
      accentColor: "#5399C2",
      lineColor: "from-brand-blue",
      iconColor: "text-brand-blue",
      ctaStyle: "border-brand-blue/30 bg-brand-blue/[0.08] text-brand-blue hover:bg-brand-blue/15",
      highlighted: false,
    },
    {
      name: "Pro",
      priceKey: isAnnual ? 'proAnnual' : 'pro',
      annualTotalMAD: "19 188 MAD/an",
      annualTotalEUR: "1 812 €/an",
      annualTotalUSD: "1 908 $/an",
      features: [
        lang === 'fr' ? "Pack Starter + limite de messages étendue" : "Starter Pack + extended message limit",
        lang === 'fr' ? "Dashboard avancé" : "Advanced dashboard",
        lang === 'fr' ? "Connexions API (CRM, email, n8n)" : "API Connections (CRM, email, n8n)",
        lang === 'fr' ? "10 000 conversations / mois" : "10,000 conversations / month",
      ],
      icon: Rocket,
      accentColor: "#E0C568",
      lineColor: "from-brand-gold",
      iconColor: "text-brand-gold",
      ctaStyle: "bg-gradient-to-r from-brand-gold to-brand-green text-black font-bold border-transparent hover:shadow-[0_0_30px_rgba(224,197,104,0.4)]",
      highlighted: true,
      badge: lang === 'fr' ? "Populaire" : "Popular",
    },
    {
      name: "Enterprise",
      isCustom: true,
      features: [
        lang === 'fr' ? "Pack Pro + fonctions sur mesure" : "Pro Pack + custom features",
        lang === 'fr' ? "Agents et conversations illimités" : "Unlimited agents and conversations",
        lang === 'fr' ? "Intégrations complexes sur-mesure" : "Complex custom integrations",
        lang === 'fr' ? "Serveurs dédiés" : "Dedicated servers",
      ],
      icon: Crown,
      accentColor: "#54A133",
      lineColor: "from-brand-green",
      iconColor: "text-brand-green",
      ctaStyle: "border-brand-green/30 bg-brand-green/[0.08] text-brand-green hover:bg-brand-green/15",
      highlighted: false,
    },
  ];

  const getAnnualTotal = (plan) => {
    if (currency === 'MAD') return plan.annualTotalMAD;
    if (currency === 'EUR') return plan.annualTotalEUR;
    return plan.annualTotalUSD;
  };

  return (
    <section data-testid="text-agent-pricing" className="relative py-16 bg-[#0A0A0A]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Section Title */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-blue/30 bg-brand-blue/10 text-brand-blue text-sm font-medium mb-4">
            <MessageSquare size={16} />
            {lang === 'fr' ? 'Agent IA Textuel' : 'Text AI Agent'}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            {lang === 'fr' ? 'Plateforme Agent IA Textuel' : 'Text AI Agent Platform'}
          </h2>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {plans.map((plan, i) => {
            const Icon = plan.icon;
            const price = plan.isCustom 
              ? (lang === 'fr' ? 'Sur devis' : 'Custom quote')
              : getPrice(plan.priceKey);

            return (
              <motion.div key={i} variants={staggerChild}>
                <div className={`relative overflow-hidden rounded-3xl bg-white/[0.03] backdrop-blur-xl border transition-all duration-300 p-8 flex flex-col h-full ${
                  plan.highlighted
                    ? 'border-brand-gold/50 shadow-[0_0_50px_rgba(224,197,104,0.15)] scale-[1.02]'
                    : 'border-white/[0.08] hover:border-white/[0.15]'
                }`}
                  data-testid={`text-plan-${i}`}
                >
                  {/* Badge for highlighted plan */}
                  {plan.highlighted && plan.badge && (
                    <div className="absolute -top-px left-1/2 -translate-x-1/2">
                      <div className="px-4 py-1.5 rounded-b-lg bg-gradient-to-r from-brand-gold to-brand-green text-black text-xs font-bold">
                        {plan.badge}
                      </div>
                    </div>
                  )}

                  {/* Top accent line */}
                  <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${plan.lineColor} to-transparent`} />

                  <Icon size={28} className={`${plan.iconColor} mb-4`} strokeWidth={1.5} />
                  <h3 className="text-2xl font-bold text-white mb-6">{plan.name}</h3>

                  {/* Price */}
                  <div className="mb-8 h-20 flex flex-col justify-end">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`${isAnnual}-${i}-${currency}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ ...SPRING }}
                      >
                        {plan.isCustom ? (
                          <span className="text-2xl font-bold text-white">{price}</span>
                        ) : (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-4xl md:text-5xl font-bold text-white">{price}</span>
                              <span className="text-base text-neutral-500">{getCurrencySymbol()} / {lang === 'fr' ? 'mois' : 'month'}</span>
                            </div>
                            {isAnnual && (
                              <p className="text-sm text-neutral-500 mt-1">{getAnnualTotal(plan)}</p>
                            )}
                          </div>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Features */}
                  <div className="space-y-4 mb-8 flex-1">
                    {plan.features.map((feature, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <Check size={18} className={plan.iconColor} strokeWidth={2.5} />
                        <span className="text-sm text-neutral-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Link
                    to="/contact"
                    data-testid={`text-cta-${i}`}
                    className={`w-full text-center py-4 rounded-xl text-sm font-semibold transition-all duration-300 border block ${plan.ctaStyle}`}
                  >
                    {plan.isCustom 
                      ? (lang === 'fr' ? 'Nous contacter' : 'Contact us')
                      : (lang === 'fr' ? `Choisir ${plan.name}` : `Choose ${plan.name}`)
                    }
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
